/**
 * trajectoryView.ts — the baked-trajectory debug view (AGENTS.md).
 *
 * Press J to cycle through the attack clips. For the selected clip this draws,
 * anchored to the local player's root transform and aimed at the live dial:
 *
 *   • the swept blade path — one line per baked sample, hilt to tip — split
 *     into the windup/recovery and the ACTIVE window, so where the hitbox is
 *     live is visible at a glance;
 *   • the tip and hilt paths as polylines;
 *   • the sword-arm hurtbox path, elbow to hand;
 *   • a marker capsule running the clip on a loop.
 *
 * Every one of those comes from `localPoseAt` — the same call the server sweeps
 * with and the renderer draws the blade from. That is the whole point of the
 * view: if it and the swing disagree, one of them is lying about where hits
 * land.
 *
 * It used to draw the canonical arc once and rotate a GROUP to aim it, which
 * was wrong twice over. It rotated about the wrong axis, and past a quarter
 * turn `localPoseAt` MIRRORS the arc rather than rolling it further — so
 * left-side swings drew a sweep the server would never take. Reproducing a
 * transform is an invitation to drift; reading the pose function is not.
 */

import * as THREE from 'three';
import {
  CLIP_IDS,
  FEEL,
  TRAJECTORIES,
  localPoseAt,
  sampleCount,
  type ClipId,
} from '@dueler/shared';

const COLOR_SWEEP = 0x3a5a6b;
const COLOR_ACTIVE = 0x4ad9ff;
const COLOR_TIP = 0xffd479;
const COLOR_HILT = 0x9aa7b8;
const COLOR_MARKER = 0xff5a45;
const COLOR_ARM = 0x7ce38b;

/** Rebuild the paths once the dial has moved more than this, radians. */
const DIAL_EPSILON = 0.01;

export interface TrajectoryView {
  /** Advances to the next clip, or off after the last one. Returns what is showing. */
  cycle(): ClipId | undefined;
  /** Re-anchors and redraws for the live dial angle. */
  update(now: number, rootPos: THREE.Vector3, rootYaw: number, dial: number): void;
  readonly current: ClipId | undefined;
  dispose(): void;
}

export function createTrajectoryView(scene: THREE.Scene): TrajectoryView {
  // anchor: the character root — FEET, not capsule centre, because that is the
  // space the trajectories were baked in.
  const anchor = new THREE.Group();
  anchor.visible = false;
  scene.add(anchor);

  const disposables: { dispose(): void }[] = [];
  const track = <T extends { dispose(): void }>(item: T): T => {
    disposables.push(item);
    return item;
  };

  const longest = Math.max(...CLIP_IDS.map((id) => sampleCount(TRAJECTORIES[id])));

  interface Path {
    data: Float32Array;
    geometry: THREE.BufferGeometry;
  }

  /** A line whose vertex buffer is rewritten in place as the dial moves. */
  function makeLine(color: number, vertices: number, segments: boolean, opacity = 1): Path {
    const data = new Float32Array(vertices * 3);
    const geometry = track(new THREE.BufferGeometry());
    geometry.setAttribute('position', new THREE.BufferAttribute(data, 3));
    const material = track(
      new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity }),
    );
    const object = segments
      ? new THREE.LineSegments(geometry, material)
      : new THREE.Line(geometry, material);
    // The paths are rewritten whenever the dial moves and never get a correct
    // bounding sphere, so leaving culling on blinks them out at screen edges.
    object.frustumCulled = false;
    anchor.add(object);
    return { data, geometry };
  }

  const sweep = makeLine(COLOR_SWEEP, longest * 2, true, 0.35);
  const active = makeLine(COLOR_ACTIVE, longest * 2, true, 0.75);
  const arm = makeLine(COLOR_ARM, longest * 2, true, 0.5);
  const tipPath = makeLine(COLOR_TIP, longest, false);
  const hiltPath = makeLine(COLOR_HILT, longest, false);
  const paths = [sweep, active, arm, tipPath, hiltPath];

  // The marker is the sword capsule itself: radius and length straight from the
  // feel table, so what you see is the shape that will be cast.
  const markerGeo = track(new THREE.CapsuleGeometry(FEEL.SWORD_RADIUS, FEEL.SWORD_LENGTH, 4, 8));
  const markerMat = track(new THREE.MeshBasicMaterial({ color: COLOR_MARKER, wireframe: true }));
  const marker = new THREE.Mesh(markerGeo, markerMat);
  anchor.add(marker);

  let current: ClipId | undefined;
  let drawnClip: ClipId | undefined;
  let drawnDial = Number.NaN;
  let clipStart = 0;

  /** A stab is not directional, so the dial does not aim it — as `weaponPose`. */
  const rollFor = (clipId: ClipId, dial: number): number => (clipId === 'slash' ? dial : 0);

  /** Rewrites every path for `clipId` at `dial`, straight from `localPoseAt`. */
  function rebuild(clipId: ClipId, dial: number): void {
    const traj = TRAJECTORIES[clipId];
    const count = sampleCount(traj);
    const roll = rollFor(clipId, dial);

    let sweepAt = 0;
    let activeAt = 0;
    for (let i = 0; i < count; i += 1) {
      const ms = (i * 1000) / traj.hz;
      const pose = localPoseAt(traj, ms, roll);
      const { hilt, tip } = pose;

      // One line per sample, hilt → tip: the blade's silhouette through the
      // swing. Live samples go in their own buffer, so the window where the
      // hitbox actually exists is a different colour.
      const live = ms >= traj.activeStartMs && ms <= traj.activeEndMs;
      const line = [hilt[0], hilt[1], hilt[2], tip[0], tip[1], tip[2]];
      if (live) {
        active.data.set(line, activeAt);
        activeAt += 6;
      } else {
        sweep.data.set(line, sweepAt);
        sweepAt += 6;
      }

      tipPath.data.set(tip, i * 3);
      hiltPath.data.set(hilt, i * 3);

      // The sword-arm hurtbox travels with the blade; drawn so it is obvious it
      // is a real thing that can be hit, not decoration.
      arm.data.set(
        [...pose.arm.elbow, ...pose.arm.hand],
        i * 6,
      );
    }

    sweep.geometry.setDrawRange(0, sweepAt / 3);
    active.geometry.setDrawRange(0, activeAt / 3);
    arm.geometry.setDrawRange(0, count * 2);
    tipPath.geometry.setDrawRange(0, count);
    hiltPath.geometry.setDrawRange(0, count);
    for (const path of paths) path.geometry.attributes['position']!.needsUpdate = true;

    drawnClip = clipId;
    drawnDial = dial;
  }

  function show(clipId: ClipId | undefined): void {
    current = clipId;
    anchor.visible = clipId !== undefined;
    drawnDial = Number.NaN;
    if (clipId !== undefined) clipStart = performance.now();
  }

  return {
    cycle() {
      const index = current === undefined ? -1 : CLIP_IDS.indexOf(current);
      show(index + 1 < CLIP_IDS.length ? CLIP_IDS[index + 1] : undefined);
      return current;
    },

    update(now, rootPos, rootYaw, dial) {
      if (current === undefined) return;

      anchor.position.set(rootPos.x, rootPos.y - FEEL.BODY_HEIGHT / 2, rootPos.z);
      anchor.rotation.y = rootYaw;

      if (current !== drawnClip || Math.abs(dial - drawnDial) > DIAL_EPSILON) {
        rebuild(current, dial);
      }

      // The marker runs the clip on a loop so you can watch the pose travel the
      // path it is drawn on. Same call, so it must sit exactly on the sweep — if
      // it drifts off, the view is disagreeing with itself.
      const traj = TRAJECTORIES[current];
      const totalMs = (sampleCount(traj) * 1000) / traj.hz;
      const { hilt, tip } = localPoseAt(
        traj,
        (now - clipStart) % totalMs,
        rollFor(current, dial),
      );
      marker.position.set(
        (hilt[0] + tip[0]) / 2,
        (hilt[1] + tip[1]) / 2,
        (hilt[2] + tip[2]) / 2,
      );
      // Point the capsule's +Y axis down the blade.
      marker.quaternion.setFromUnitVectors(
        UP,
        BLADE.set(tip[0] - hilt[0], tip[1] - hilt[1], tip[2] - hilt[2]).normalize(),
      );
    },

    get current() {
      return current;
    },

    dispose() {
      scene.remove(anchor);
      for (const d of disposables) d.dispose();
      disposables.length = 0;
    },
  };
}

// Scratch vectors: update() runs every frame and should not allocate.
const UP = new THREE.Vector3(0, 1, 0);
const BLADE = new THREE.Vector3();
